import { z } from 'zod';
import { LATE_CANCEL_WINDOW_HOURS } from './enums.js';
import { memberSummarySchema } from './members.js';
import { roomSummarySchema } from './screenings.js';
/**
 * Seat maps and booking (DESIGN.md §8).
 *
 * ## Coordinates are fractions of the image, not pixels
 *
 * §4 originally said seats were stored in the image's natural pixel space.
 * They are stored as `x`, `y` in `[0, 1]` instead, and the difference is worth
 * the deviation:
 *
 *  - **Every read site is already the same expression.** The map renders at
 *    whatever width the sheet gives it, so a pixel coordinate would be divided
 *    by `image_w` at every single place it is drawn. Storing the fraction is
 *    storing the value everything actually wants.
 *  - **Re-shooting the room does not orphan the map.** A new photo from
 *    roughly the same spot at a different resolution leaves every seat where
 *    it was; in pixel space it needs a rescale pass over every row, and the
 *    pass has to be written before anyone dares replace an image.
 *
 * `rooms.image_w/image_h` stay, and are still worth having: they give the
 * container its aspect ratio before the image loads, which is what stops the
 * seat markers from being laid out against a zero-height box and then jumping.
 *
 * Nothing was migrated for this — no seat row has ever existed.
 */
/**
 * What `scale: 1` means: a marker one thirteenth of the room image's width.
 *
 * A *fraction of the image*, not a pixel size, and this is the whole reason it
 * is in shared rather than in a stylesheet. The editor draws the map at ~700px
 * and the booking sheet draws it at phone width; a 44px marker would be a
 * quarter of the sofa in one and a dot in the other, so seats placed shoulder
 * to shoulder in the editor would overlap on the phone. Sized against the
 * image, a map looks the same everywhere it is drawn.
 */
export const SEAT_UNIT_FRACTION = 0.075;
/** Below this a marker is smaller than a fingertip; above it, it is furniture. */
export const SEAT_MIN_SCALE = 0.4;
export const SEAT_MAX_SCALE = 3;
/**
 * Not a technical limit — a room with sixty places to sit is not a living
 * room, it is a mistake in the editor, and it is better caught by the schema
 * than by whoever opens the booking sheet.
 */
export const MAX_SEATS_PER_ROOM = 60;
export const seatSchema = z.object({
    id: z.string().uuid(),
    label: z.string(),
    /** Fractions of the room image. See the note above. */
    x: z.number(),
    y: z.number(),
    /** Degrees clockwise. Which way the seat faces, and how the avatar sits. */
    rotation: z.number(),
    scale: z.number(),
    /** Visible but permanently unbookable — a great place to hide something. */
    isBookable: z.boolean(),
    sortOrder: z.number().int(),
});
/**
 * One seat as the editor holds it. `id` absent means "new" — that is the whole
 * distinction, and it is why this is not just `seatSchema.partial()`.
 *
 * `sortOrder` is deliberately not here: it is the seat's index in the array,
 * so the order the editor shows is the order that is stored, and there is no
 * second field for the editor to keep in step with the list it already has.
 */
export const seatDraftSchema = z.object({
    id: z.string().uuid().optional(),
    label: z.string().trim().min(1, 'Mesto mora imati ime.').max(60),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    rotation: z.number().min(0).max(360).default(0),
    scale: z.number().min(SEAT_MIN_SCALE).max(SEAT_MAX_SCALE).default(1),
    isBookable: z.boolean().default(true),
});
/**
 * The whole map, saved at once.
 *
 * Per-seat routes would mean a request per drag, and a half-saved map after a
 * dropped connection. This is one array and one transaction: rows with an `id`
 * are updated, rows without are inserted, and rows whose id is *absent from
 * the array* are deleted. The editor holds the map in memory anyway, so the
 * shape it saves is the shape it already has.
 */
export const putSeatsRequest = z.object({
    seats: z.array(seatDraftSchema).max(MAX_SEATS_PER_ROOM),
});
export const roomMapSchema = z.object({
    room: roomSummarySchema.extend({
        imageUrl: z.string().nullable(),
        imageW: z.number().int().nullable(),
        imageH: z.number().int().nullable(),
    }),
    seats: z.array(seatSchema),
});
/**
 * Who is in a seat.
 *
 * `placedByHost` travels because it changes what the sheet is allowed to say:
 * a seat you were dropped into by the host is still yours to move out of, but
 * "ti si ovde" reads differently from a seat you chose, and the host wants to
 * see at a glance which of the room they filled in themselves.
 */
export const seatOccupantSchema = z.object({
    seatId: z.string().uuid(),
    user: memberSummarySchema,
    placedByHost: z.boolean(),
});
export const screeningSeatMapSchema = roomMapSchema.extend({
    screeningId: z.string().uuid(),
    taken: z.array(seatOccupantSchema),
    /** Resolved server-side. A client that has to find itself can get it wrong. */
    mySeatId: z.string().uuid().nullable(),
    /**
     * Whether the sheet may book anything at all. False for a draft, a cancelled
     * night, and a film whose credits have rolled — the map is still worth
     * *showing* in all three cases, which is why this is a flag and not a 404.
     */
    open: z.boolean(),
});
/**
 * Claim a seat, or move to one.
 *
 * The same request for both, because there is one row per person per screening
 * and moving is that row changing its `seat_id`. Two routes would mean the
 * client deciding which of them applies from state it may not have — and a
 * "book" that discovers you already had a seat has to do the move anyway.
 */
export const reserveRequest = z.object({
    seatId: z.string().uuid(),
    /**
     * Host only: drop a member into a free seat (DESIGN.md §8). Omitted is
     * yourself, which is what every member sending this means.
     */
    userId: z.string().uuid().nullable().default(null),
});
export const cancelReservationRequest = z.object({
    userId: z.string().uuid().nullable().default(null),
});
export const reserveResponse = z.object({
    seatId: z.string().uuid(),
    /** The seat this replaced, when it was a move. Null on a first booking. */
    freedSeatId: z.string().uuid().nullable(),
});
export const cancelResponse = z.object({
    seatId: z.string().uuid(),
    /** Frozen at cancel time. See `isLateCancel` below. */
    wasLateCancel: z.boolean(),
});
/**
 * Whether cancelling *now* counts against you.
 *
 * The server calls this once, at cancel time, and freezes the answer into
 * `reservations.was_late_cancel` (DESIGN.md §4). It is exported because the
 * sheet warns you before you tap — being told afterwards that the site has
 * quietly filed you under "Serijski otkazivač" is a worse joke than the one
 * the feature is going for.
 */
export function isLateCancel(startsAt, now = new Date()) {
    const at = typeof startsAt === 'string' ? new Date(startsAt) : startsAt;
    if (Number.isNaN(at.getTime()))
        return false;
    // Negative is included on purpose: cancelling after the film started is the
    // latest a cancellation can possibly be.
    return at.getTime() - now.getTime() < LATE_CANCEL_WINDOW_HOURS * 3_600_000;
}
//# sourceMappingURL=seats.js.map